import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import {
  getCurrentPeriod,
  calculateBudgetSummary,
} from '../lib/budgetCalculator'
import { safeNotify } from '../lib/notifications'

export function useBudget(householdId: string | null) {
  const queryClient = useQueryClient()
  const period      = getCurrentPeriod()

  // ── Monthly budget setting ─────────────────────────────────────────────────

  const { data: budget = null } = useQuery({
    queryKey: ['budget', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('households')
        .select('monthly_budget')
        .eq('id', householdId!)
        .single()
      return (data?.monthly_budget as number | null) ?? null
    },
  })

  // ── Current period confirmations (with category) ───────────────────────────

  const { data: confirmations = [] } = useQuery({
    queryKey: ['budget-confirmations', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select(`
          id,
          price_paid,
          total_paid,
          confirmed_at,
          item_id,
          store_name,
          pantry_items(display_category, name, emoji)
        `)
        .eq('household_id', householdId!)
        .gte('confirmed_at', period.start.toISOString())
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: false })
      return data ?? []
    },
  })

  // ── Last month confirmations (for MoM delta) ───────────────────────────────

  const { data: lastMonthConfirmations = [] } = useQuery({
    queryKey: ['last-month-confirmations', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const lastMonth = new Date()
      lastMonth.setMonth(lastMonth.getMonth() - 1)
      const start = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1)
      const end   = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0)
      const { data } = await supabase
        .from('confirmations')
        .select('price_paid, total_paid, confirmed_at')
        .eq('household_id', householdId!)
        .gte('confirmed_at', start.toISOString())
        .lte('confirmed_at', end.toISOString())
        .or('price_paid.not.is.null,total_paid.not.is.null')
      return data ?? []
    },
  })

  // ── Set budget mutation ────────────────────────────────────────────────────

  const setBudgetMutation = useMutation({
    mutationFn: async (amount: number) => {
      if (!householdId) return
      const { error } = await supabase
        .from('households')
        .update({ monthly_budget: amount })
        .eq('id', householdId!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget', householdId] })
    },
  })

  // ── Derived values ─────────────────────────────────────────────────────────

  const summary = budget != null
    ? calculateBudgetSummary(budget, confirmations, period)
    : null

  const lastMonthSpend = lastMonthConfirmations.reduce(
    (sum, c) => sum + ((c.total_paid as number) || (c.price_paid as number) || 0), 0,
  )
  const monthOverMonthDelta = summary ? summary.spent - lastMonthSpend : null

  // ── Budget warning notification ────────────────────────────────────────────

  useEffect(() => {
    if (!summary) return
    if (!summary.isNearLimit) return

    const key = `budget-warning-${period.label}`
    if (localStorage.getItem(key)) return

    // Fire-and-forget — errors must not bubble up and crash the app
    safeNotify('Budget alert 🔶', {
      body: `You have used ${Math.round(summary.percentUsed)}% of your ${period.label} grocery budget. $${summary.remaining.toFixed(2)} remaining.`,
      icon: '/pwa-192.png',
      tag:  'budget-warning',
    })
      .then(() => { localStorage.setItem(key, 'true') })
      .catch((err) => { console.warn('Budget notification failed:', err) })
  }, [summary, period.label])

  return {
    budget,
    summary,
    confirmations,
    period,
    lastMonthSpend,
    monthOverMonthDelta,
    setBudget:        setBudgetMutation.mutate,
    isSettingBudget:  setBudgetMutation.isPending,
  }
}
